import { type Container, Graphics, type Sprite } from 'pixi.js';
import type { ClassDrawing, PaintedDrawing } from '../drawings';
import { type Shape, paintSheet } from '../paint';
import type { Look } from '../player-look';
import type { Textures } from '../textures';
import { REFERENCE_RADIUS } from '../textures-looks';
import { add, hide, placeOutline, setTint } from '../util';
import { CAPE_STARS, Cape, TAILS } from './celeste-cape';
import {
  CELESTE_PIECES,
  HANDS,
  HELD_STAR,
  HELMET,
  SPARKLE,
  STAR_TINTS,
  UNIT,
} from './celeste-paint';
import { popOf, twinkle } from './constellations';

type Parts = Readonly<Record<keyof typeof CELESTE_PIECES, Shape>>;

const EXTENT = 46;
const MIDDLE = -10;
const GALAXY_TURN = { beats: -0.42, calm: 0.3 } as const;
const HELD = { back: 0.3, glow: 26, glowAlpha: 0.35, beatAlpha: 0.35 } as const;

function createStarLook(
  bodies: Container,
  glows: Container,
  textures: Textures,
  parts: Parts,
): Look {
  const capeOutline = bodies.addChild(new Graphics());
  const outline = add(bodies, parts.silhouette);
  const cape = bodies.addChild(new Graphics());
  const tails = new Cape();
  const hues = [parts.star0, parts.star1, parts.star2, parts.star3];
  const hueOf = (index: number): Shape => hues[index % hues.length] ?? parts.star0;
  const capeStars = TAILS.flatMap((_, tail) =>
    CAPE_STARS.steps.map((_step, index) => {
      const sprite = add(bodies, hueOf(tail * CAPE_STARS.steps.length + index));
      sprite.blendMode = 'add';
      return sprite;
    }),
  );
  const torso = add(bodies, parts.torso);
  const leftHand = add(bodies, parts.hand);
  const heldGlow = add(glows, textures.halo);
  const rightHand = add(bodies, parts.hand);
  const held = add(bodies, hueOf(HELD_STAR.hue));
  const helmet = add(bodies, parts.helmet);
  const galaxy = add(bodies, parts.galaxy);
  const body = [...capeStars, torso, leftHand, rightHand, helmet, galaxy];
  let originX = 0;
  let originY = 0;
  let cos = 1;
  let sin = 0;
  let size = 1;
  let facing = 0;

  function put(sprite: Sprite, along: number, across: number, turn = 0): void {
    sprite.position.set(
      originX + (along * cos - across * sin) * UNIT * size,
      originY + (along * sin + across * cos) * UNIT * size,
    );
    sprite.rotation = facing + turn;
    sprite.scale.set(size);
  }

  function sparkle(sprite: Sprite, along: number, across: number, scale: number): void {
    put(sprite, along, across);
    sprite.scale.set((SPARKLE.points * scale * UNIT * size) / parts.star0.radius);
  }

  return {
    reset() {
      tails.reset();
    },
    hide() {
      hide(...body, outline, held, heldGlow);
      cape.visible = capeOutline.visible = false;
    },
    place(input) {
      const { x, y, angle, scale, color, frame } = input;
      const { now, calm, light, tempo } = frame;
      const beats = (now - tempo.originTick) / tempo.ticksPerBeat;
      originX = x;
      originY = y;
      cos = Math.cos(angle);
      sin = Math.sin(angle);
      size = scale;
      facing = angle;
      for (const sprite of body) {
        sprite.visible = true;
      }

      tails.place({
        x,
        y,
        facing: angle,
        size: scale,
        heading: input.heading,
        moving: input.moving,
        speed: input.speed,
        beats,
        dt: input.dt,
        calm,
      });
      cape.visible = true;
      capeOutline.visible = !light.additive;
      tails.draw(cape, capeOutline.visible ? capeOutline : null, color, frame.palette.texte, scale);
      tails.middles.forEach((middle, tail) => {
        CAPE_STARS.steps.forEach((step, index) => {
          const star = capeStars[tail * CAPE_STARS.steps.length + index];
          if (star === undefined) {
            return;
          }
          const twinkled = calm ? 1 : twinkle(beats, tail * CAPE_STARS.steps.length + index);
          star.position.set(middle[step * 2] ?? x, middle[step * 2 + 1] ?? y);
          star.rotation = facing;
          star.scale.set(
            (SPARKLE.points * CAPE_STARS.sparkle * twinkled * UNIT * size) / parts.star0.radius,
          );
          setTint(star, 0xffffff);
        });
      });

      put(torso, 0, 0);
      placeOutline(outline, torso, parts.silhouette.texture, REFERENCE_RADIUS, frame);
      setTint(torso, 0xffffff);
      put(leftHand, HANDS.left[0], HANDS.left[1]);
      setTint(leftHand, input.teint);
      put(rightHand, HANDS.right[0], HANDS.right[1]);
      setTint(rightHand, input.teint);

      const thrown =
        input.fireCount > 0 ? input.sinceFire / tempo.ticksPerBeat - HELD.back : Infinity;
      const pop = thrown < 0 ? 0 : calm ? 1 : popOf(thrown);
      held.visible = heldGlow.visible = pop > 0;
      if (pop > 0) {
        sparkle(held, HELD_STAR.along, HELD_STAR.across, HELD_STAR.sparkle * pop);
        setTint(held, 0xffffff);
        put(heldGlow, HELD_STAR.along, HELD_STAR.across);
        heldGlow.scale.set(
          (HELD.glow * HELD_STAR.sparkle * pop * UNIT * size) / textures.halo.radius,
        );
        heldGlow.alpha = light.haloAlpha * (HELD.glowAlpha + HELD.beatAlpha * frame.pulse);
        setTint(heldGlow, STAR_TINTS[HELD_STAR.hue] ?? 0xffffff);
      }

      put(helmet, HELMET.along, 0);
      setTint(helmet, 0xffffff);
      put(galaxy, HELMET.along, 0, beats * GALAXY_TURN.beats * (calm ? GALAXY_TURN.calm : 1));
      setTint(galaxy, 0xffffff);
      return 0;
    },
  };
}

export const CELESTE_DRAWING: ClassDrawing = {
  classId: 'celeste',
  paint(density): PaintedDrawing {
    const { shapes, source } = paintSheet(CELESTE_PIECES, density);
    return {
      extent: EXTENT,
      height: 1,
      centerAhead: MIDDLE,
      shadow: shapes.shadow,
      source,
      create: (bodies: Container, glows: Container, textures: Textures) =>
        createStarLook(bodies, glows, textures, shapes),
    };
  },
};
